/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from "react";
import {
  getPendingDepositsByAdmin,
  getUnverifiedDepositsByAdmin,
  getVerifiedDepositsByAdmin,
  markDepositedToBankByAdmin,
  verifyBankDepositByAdmin,
  exportVerifiedDepositsByAdmin,
} from "../api/subscriptionsApi";
import AccessControl from "../components/AccessControl";

// ─── Inline styles ────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f5f6fa",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: "24px 20px 48px",
    boxSizing: "border-box",
  },
  wrap: { maxWidth: 1140, margin: "0 auto" },

  // Header
  headerTop: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 4,
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
  },
  h1: { margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" },
  subtitle: {
    margin: "0 0 24px",
    fontSize: 13.5,
    color: "#6b7280",
    paddingLeft: 56,
  },

  // Flow indicator
  flow: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    background: "#fff",
    borderRadius: 12,
    padding: "14px 22px",
    boxShadow: "0 1px 3px rgba(0,0,0,.07)",
    marginBottom: 24,
    overflowX: "auto",
  },
  flowStep: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  flowArrow: {
    color: "#d1d5db",
    fontSize: 18,
    padding: "0 4px",
    flexShrink: 0,
  },

  // Date filter bar
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    background: "#fff",
    borderRadius: 12,
    padding: "14px 18px",
    boxShadow: "0 1px 3px rgba(0,0,0,.07)",
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "#6b7280",
    whiteSpace: "nowrap",
  },
  filterInput: {
    padding: "7px 11px",
    fontSize: 13,
    border: "1.5px solid #e5e7eb",
    borderRadius: 7,
    outline: "none",
    fontFamily: "inherit",
    color: "#111827",
    background: "#f9fafb",
  },
  btnApply: {
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 600,
    background: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
  },
  btnClear: {
    padding: "7px 14px",
    fontSize: 13,
    fontWeight: 600,
    background: "#f3f4f6",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 7,
    cursor: "pointer",
  },
  btnExcel: {
    marginLeft: "auto",
    padding: "7px 16px",
    fontSize: 13,
    fontWeight: 600,
    background: "#16a34a",
    color: "#fff",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },

  // Tabs
  tabs: {
    display: "flex",
    gap: 0,
    background: "#fff",
    borderRadius: 12,
    padding: 6,
    boxShadow: "0 1px 3px rgba(0,0,0,.07)",
    marginBottom: 20,
    width: "fit-content",
  },
  tab: {
    padding: "9px 20px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    background: "transparent",
    color: "#6b7280",
    transition: "all .15s",
  },
  tabActive: {
    background: "#eff6ff",
    color: "#1d4ed8",
  },

  // Summary strip
  strip: {
    display: "flex",
    gap: 20,
    alignItems: "center",
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: 10,
    padding: "10px 16px",
    marginBottom: 16,
    fontSize: 13,
    color: "#0369a1",
    flexWrap: "wrap",
  },
  stripBold: { fontWeight: 700 },

  // Table card
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 1px 4px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "11px 14px",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: ".05em",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
  },
  td: {
    padding: "12px 14px",
    fontSize: 13.5,
    color: "#374151",
    borderBottom: "1px solid #f3f4f6",
  },
  driverName: { fontWeight: 600, color: "#111827", margin: 0, fontSize: 13.5 },
  driverPhone: { margin: 0, fontSize: 12, color: "#9ca3af", marginTop: 2 },
  badge: {
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    background: "#ede9fe",
    color: "#6d28d9",
  },
  amount: { fontWeight: 700, color: "#111827", fontSize: 14 },
  muted: { color: "#d1d5db" },
  receiptTag: {
    fontSize: 11.5,
    fontWeight: 600,
    color: "#0369a1",
    background: "#e0f2fe",
    padding: "2px 7px",
    borderRadius: 5,
  },
  verifiedBadge: {
    fontSize: 11.5,
    fontWeight: 700,
    color: "#15803d",
    background: "#dcfce7",
    padding: "3px 9px",
    borderRadius: 6,
  },

  btnDeposit: {
    padding: "6px 13px",
    fontSize: 12.5,
    fontWeight: 600,
    background: "#dbeafe",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    borderRadius: 7,
    cursor: "pointer",
  },
  btnVerify: {
    padding: "6px 13px",
    fontSize: 12.5,
    fontWeight: 600,
    background: "#dcfce7",
    color: "#15803d",
    border: "1px solid #bbf7d0",
    borderRadius: 7,
    cursor: "pointer",
  },

  // Empty
  empty: { textAlign: "center", padding: "48px 24px", color: "#9ca3af" },
  emptyIcon: { fontSize: 38, marginBottom: 10 },
  emptyTitle: { fontWeight: 600, color: "#374151", margin: "0 0 4px" },
  emptySub: { fontSize: 13, margin: 0 },

  // Skeleton
  skeleton: {
    padding: "20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  skRow: { height: 52, background: "#f3f4f6", borderRadius: 8 },

  // Toast
  toast: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 9999,
    background: "#1e293b",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: 500,
    boxShadow: "0 4px 16px rgba(0,0,0,.25)",
    maxWidth: 360,
    borderLeft: "4px solid #22c55e",
  },

  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    zIndex: 9000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 440,
    boxShadow: "0 20px 60px rgba(0,0,0,.25)",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "18px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #f3f4f6",
  },
  modalTitle: { margin: 0, fontSize: 16, fontWeight: 700 },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    color: "#9ca3af",
    lineHeight: 1,
    padding: 0,
  },
  modalBody: { padding: "4px 22px 22px" },
  banner: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 9,
    padding: "10px 14px",
    marginBottom: 14,
    marginTop: 14,
    fontSize: 13,
    color: "#1e40af",
  },
  detailCard: {
    background: "#f9fafb",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    padding: "12px 16px",
    marginBottom: 14,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "5px 0",
  },
  detailLabel: { fontSize: 12.5, color: "#9ca3af", fontWeight: 500 },
  detailValue: { fontSize: 13.5, color: "#111827", fontWeight: 600 },
  detailAmount: { fontSize: 18, fontWeight: 800, color: "#111827" },
  fieldLabel: {
    display: "block",
    fontSize: 12.5,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  fieldInput: {
    width: "100%",
    padding: "9px 12px",
    fontSize: 13.5,
    border: "1.5px solid #e5e7eb",
    borderRadius: 8,
    outline: "none",
    boxSizing: "border-box",
    color: "#111827",
    fontFamily: "inherit",
  },
  modalActions: { display: "flex", gap: 10, marginTop: 18 },
  btnCancel: {
    flex: 1,
    padding: "10px",
    fontSize: 13.5,
    fontWeight: 600,
    background: "#f3f4f6",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    cursor: "pointer",
  },
  btnBank: {
    flex: 2,
    padding: "10px",
    fontSize: 13.5,
    fontWeight: 700,
    background: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  btnVerifyMd: {
    flex: 2,
    padding: "10px",
    fontSize: 13.5,
    fontWeight: 700,
    background: "linear-gradient(135deg,#22c55e,#16a34a)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
};

// ─── Utility ──────────────────────────────────────────────────────────────────
function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function today() {
  return new Date().toISOString().split("T")[0];
}

// ─── Custom hook: manual fetch with params ────────────────────────────────────
function useManualFetch(apiFn) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFn = useCallback(
    async (params = {}) => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFn(params);
        if (res?.success === false) throw new Error(res.error || "Failed");
        setData(res);
      } catch (e) {
        setError(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [apiFn],
  );

  return { data, loading, error, fetch: fetchFn };
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 1 — Pending Deposits
// ════════════════════════════════════════════════════════════════════════════
function PendingDepositsTab({ showToast }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({});

  const { data, loading, error, fetch: fetchData } = useManualFetch(
    getPendingDepositsByAdmin,
  );

  // initial load
  useEffect(() => {
    fetchData({});
  }, [fetchData]);

  const handleApply = () => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    setApplied(p);
    fetchData(p);
  };
  const handleClear = () => {
    setFrom("");
    setTo("");
    setApplied({});
    fetchData({});
  };

  const rows = data?.data ?? (Array.isArray(data) ? data : []);
  const totalAmt =
    data?.totalAmount ?? rows.reduce((s, r) => s + (r.amount || 0), 0);

  const [selected, setSelected] = useState(null);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const adminId = localStorage.getItem("adminId");

  const handleMarkDeposited = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await markDepositedToBankByAdmin({
        paymentId: selected._id,
        adminId,
        receiptNumber: receiptNumber.trim() || undefined,
      });
      if (res?.success === false) throw new Error(res.error || "Failed");
      showToast("Marked as deposited. Awaiting superadmin verification.");
      setSelected(null);
      setReceiptNumber("");
      fetchData(applied);
    } catch (e) {
      showToast(e?.message || "Something went wrong", false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={S.filterBar}>
        <span style={S.filterLabel}>Date Range</span>
        <input
          style={S.filterInput}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <span style={{ color: "#9ca3af", fontSize: 13 }}>to</span>
        <input
          style={S.filterInput}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          max={today()}
        />
        <button style={S.btnApply} onClick={handleApply}>
          Apply
        </button>
        {(from || to) && (
          <button style={S.btnClear} onClick={handleClear}>
            Clear
          </button>
        )}
      </div>

      {/* Summary */}
      {rows.length > 0 && (
        <div style={S.strip}>
          <span>
            {rows.length} payment{rows.length !== 1 ? "s" : ""}
          </span>
          <span style={{ color: "#93c5fd" }}>·</span>
          <span>
            Total pending:{" "}
            <span style={S.stripBold}>₹{totalAmt?.toLocaleString()}</span>
          </span>
          {(applied.from || applied.to) && (
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              ({applied.from || "start"} → {applied.to || "today"})
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={S.skeleton}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={S.skRow} />
          ))}
        </div>
      ) : error ? (
        <div style={S.empty}>
          <p style={S.emptyTitle}>⚠️ {error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={S.emptyIcon}>🏦</div>
          <p style={S.emptyTitle}>All deposited!</p>
          <p style={S.emptySub}>
            No confirmed cash waiting to be sent to the bank.
          </p>
        </div>
      ) : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  "Driver",
                  "Plan",
                  "Amount",
                  "Collected By",
                  "Confirmed At",
                  "Action",
                ].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={S.td}>
                    <p style={S.driverName}>{r.driverId?.name || "—"}</p>
                    <p style={S.driverPhone}>{r.driverId?.phone || ""}</p>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge}>{r.planId?.name || "—"}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.amount}>₹{r.amount?.toLocaleString()}</span>
                  </td>
                  <td style={S.td}>
                    {r.collectedBy?.name || <span style={S.muted}>—</span>}
                  </td>
                  <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                    {fmt(r.paidAt)}
                  </td>
                  <td style={S.td}>
                    <button style={S.btnDeposit} onClick={() => setSelected(r)}>
                      🏦 Mark Deposited
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.modalHeader, background: "#eff6ff" }}>
              <h3 style={{ ...S.modalTitle, color: "#1d4ed8" }}>
                🏦 Mark as Deposited to Bank
              </h3>
              <button style={S.modalClose} onClick={() => setSelected(null)}>
                ×
              </button>
            </div>
            <div style={S.modalBody}>
              <div style={S.banner}>
                <span>💡</span>
                <p style={{ margin: 0 }}>
                  Confirm this cash has been physically deposited. Superadmin
                  will verify after.
                </p>
              </div>
              <div style={S.detailCard}>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Driver</span>
                  <span style={S.detailValue}>{selected.driverId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Phone</span>
                  <span style={S.detailValue}>{selected.driverId?.phone}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Plan</span>
                  <span style={S.badge}>{selected.planId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Amount</span>
                  <span style={S.detailAmount}>
                    ₹{selected.amount?.toLocaleString()}
                  </span>
                </div>
              </div>
              <label style={S.fieldLabel}>
                Receipt / Reference Number (optional)
              </label>
              <input
                style={S.fieldInput}
                type="text"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="e.g. HDFC2024061101"
              />
              <div style={S.modalActions}>
                <button style={S.btnCancel} onClick={() => setSelected(null)}>
                  Cancel
                </button>
                <button
                  style={S.btnBank}
                  onClick={handleMarkDeposited}
                  disabled={busy}
                >
                  {busy ? "Saving…" : "🏦 Confirm Deposit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 2 — Unverified Deposits (Superadmin verifies)
// ════════════════════════════════════════════════════════════════════════════
function UnverifiedDepositsTab({ showToast }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({});

  const { data, loading, error, fetch: fetchDeposits } = useManualFetch(
    getUnverifiedDepositsByAdmin,
  );

  useEffect(() => {
    fetchDeposits({});
  }, [fetchDeposits]);

  const handleApply = () => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    setApplied(p);
    fetchDeposits(p);
  };
  const handleClear = () => {
    setFrom("");
    setTo("");
    setApplied({});
    fetchDeposits({});
  };

  const rows = data?.data ?? (Array.isArray(data) ? data : []);
  const totalAmt =
    data?.totalAmount ?? rows.reduce((s, r) => s + (r.amount || 0), 0);

  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const superadminId = localStorage.getItem("adminId");

  const handleVerify = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await verifyBankDepositByAdmin({
        paymentId: selected._id,
        superadminId,
      });
      if (res?.success === false) throw new Error(res.error || "Failed");
      showToast("Bank deposit verified successfully.");
      setSelected(null);
      fetchDeposits(applied);
    } catch (e) {
      showToast(e?.message || "Something went wrong", false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={S.filterBar}>
        <span style={S.filterLabel}>Date Range</span>
        <input
          style={S.filterInput}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <span style={{ color: "#9ca3af", fontSize: 13 }}>to</span>
        <input
          style={S.filterInput}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          max={today()}
        />
        <button style={S.btnApply} onClick={handleApply}>
          Apply
        </button>
        {(from || to) && (
          <button style={S.btnClear} onClick={handleClear}>
            Clear
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <div style={S.strip}>
          <span>
            {rows.length} deposit{rows.length !== 1 ? "s" : ""}
          </span>
          <span style={{ color: "#93c5fd" }}>·</span>
          <span>
            Total:{" "}
            <span style={S.stripBold}>₹{totalAmt?.toLocaleString()}</span>
          </span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            awaiting your verification
          </span>
        </div>
      )}

      {loading ? (
        <div style={S.skeleton}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={S.skRow} />
          ))}
        </div>
      ) : error ? (
        <div style={S.empty}>
          <p style={S.emptyTitle}>⚠️ {error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={S.emptyIcon}>✅</div>
          <p style={S.emptyTitle}>All verified!</p>
          <p style={S.emptySub}>No pending verifications for this period.</p>
        </div>
      ) : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  "Driver",
                  "Plan",
                  "Amount",
                  "Collected By",
                  "Deposited By",
                  "Deposited At",
                  "Receipt #",
                  "Action",
                ].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={S.td}>
                    <p style={S.driverName}>{r.driverId?.name || "—"}</p>
                    <p style={S.driverPhone}>{r.driverId?.phone || ""}</p>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge}>{r.planId?.name || "—"}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.amount}>₹{r.amount?.toLocaleString()}</span>
                  </td>
                  <td style={S.td}>
                    {r.collectedBy?.name || <span style={S.muted}>—</span>}
                  </td>
                  <td style={S.td}>
                    {r.depositedBy?.name || <span style={S.muted}>—</span>}
                  </td>
                  <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                    {fmt(r.depositedAt)}
                  </td>
                  <td style={S.td}>
                    {r.receiptNumber ? (
                      <span style={S.receiptTag}>{r.receiptNumber}</span>
                    ) : (
                      <span style={S.muted}>—</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <button style={S.btnVerify} onClick={() => setSelected(r)}>
                      ✅ Verify
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Verify Modal */}
      {selected && (
        <div style={S.overlay} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...S.modalHeader, background: "#f0fdf4" }}>
              <h3 style={{ ...S.modalTitle, color: "#15803d" }}>
                ✅ Verify Bank Deposit
              </h3>
              <button style={S.modalClose} onClick={() => setSelected(null)}>
                ×
              </button>
            </div>
            <div style={S.modalBody}>
              <div
                style={{
                  ...S.banner,
                  background: "#f0fdf4",
                  borderColor: "#bbf7d0",
                  color: "#14532d",
                }}
              >
                <span>🔍</span>
                <p style={{ margin: 0 }}>
                  Confirm that the physical bank deposit matches the amount
                  below. This action cannot be undone.
                </p>
              </div>
              <div style={S.detailCard}>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Driver</span>
                  <span style={S.detailValue}>{selected.driverId?.name}</span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Amount</span>
                  <span style={S.detailAmount}>
                    ₹{selected.amount?.toLocaleString()}
                  </span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Deposited By</span>
                  <span style={S.detailValue}>
                    {selected.depositedBy?.name || "—"}
                  </span>
                </div>
                <div style={S.detailRow}>
                  <span style={S.detailLabel}>Deposited At</span>
                  <span style={{ ...S.detailValue, fontSize: 12.5 }}>
                    {fmt(selected.depositedAt)}
                  </span>
                </div>
                {selected.receiptNumber && (
                  <div style={S.detailRow}>
                    <span style={S.detailLabel}>Receipt #</span>
                    <span style={S.receiptTag}>{selected.receiptNumber}</span>
                  </div>
                )}
              </div>
              <div style={S.modalActions}>
                <button style={S.btnCancel} onClick={() => setSelected(null)}>
                  Cancel
                </button>
                <button
                  style={S.btnVerifyMd}
                  onClick={handleVerify}
                  disabled={busy}
                >
                  {busy ? "Verifying…" : "✅ Confirm Verification"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 3 — Verified Deposits (records + Excel export)
// ════════════════════════════════════════════════════════════════════════════
function VerifiedDepositsTab({ showToast }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({});
  const [exporting, setExporting] = useState(false);

  const { data, loading, error, fetch: fetchVerified } = useManualFetch(
    getVerifiedDepositsByAdmin,
  );
  useEffect(() => {
    fetchVerified({});
  }, [fetchVerified]);

  const handleApply = () => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    setApplied(p);
    fetchVerified(p);
  };
  const handleClear = () => {
    setFrom("");
    setTo("");
    setApplied({});
    fetchVerified({});
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportVerifiedDepositsByAdmin(applied);
      if (res?.success === false) throw new Error(res.error || "Export failed");
      showToast("Excel file downloaded successfully.");
    } catch (e) {
      showToast(e?.message || "Export failed", false);
    } finally {
      setExporting(false);
    }
  };

  const rows = data?.data ?? (Array.isArray(data) ? data : []);
  const totalAmt =
    data?.totalAmount ?? rows.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div>
      <div style={S.filterBar}>
        <span style={S.filterLabel}>Date Range</span>
        <input
          style={S.filterInput}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <span style={{ color: "#9ca3af", fontSize: 13 }}>to</span>
        <input
          style={S.filterInput}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          max={today()}
        />
        <button style={S.btnApply} onClick={handleApply}>
          Apply
        </button>
        {(from || to) && (
          <button style={S.btnClear} onClick={handleClear}>
            Clear
          </button>
        )}
        <button style={S.btnExcel} onClick={handleExport} disabled={exporting}>
          {exporting ? "⏳ Exporting…" : "📥 Export Excel"}
        </button>
      </div>

      {rows.length > 0 && (
        <div style={S.strip}>
          <span>
            {rows.length} verified deposit{rows.length !== 1 ? "s" : ""}
          </span>
          <span style={{ color: "#93c5fd" }}>·</span>
          <span>
            Total verified:{" "}
            <span style={S.stripBold}>₹{totalAmt?.toLocaleString()}</span>
          </span>
          {(applied.from || applied.to) && (
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              ({applied.from || "start"} → {applied.to || "today"})
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div style={S.skeleton}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={S.skRow} />
          ))}
        </div>
      ) : error ? (
        <div style={S.empty}>
          <p style={S.emptyTitle}>⚠️ {error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={S.emptyIcon}>📋</div>
          <p style={S.emptyTitle}>No verified deposits found</p>
          <p style={S.emptySub}>Try a different date range.</p>
        </div>
      ) : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  "Driver",
                  "Plan",
                  "Amount",
                  "Collected By",
                  "Deposited By",
                  "Deposited At",
                  "Verified At",
                  "Receipt #",
                  "Status",
                ].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id}>
                  <td style={S.td}>
                    <p style={S.driverName}>{r.driverId?.name || "—"}</p>
                    <p style={S.driverPhone}>{r.driverId?.phone || ""}</p>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge}>{r.planId?.name || "—"}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.amount}>₹{r.amount?.toLocaleString()}</span>
                  </td>
                  <td style={S.td}>
                    {r.collectedBy?.name || <span style={S.muted}>—</span>}
                  </td>
                  <td style={S.td}>
                    {r.depositedBy?.name || <span style={S.muted}>—</span>}
                  </td>
                  <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                    {fmt(r.depositedAt)}
                  </td>
                  <td style={{ ...S.td, fontSize: 12.5, color: "#6b7280" }}>
                    {fmt(r.bankVerifiedAt)}
                  </td>
                  <td style={S.td}>
                    {r.receiptNumber ? (
                      <span style={S.receiptTag}>{r.receiptNumber}</span>
                    ) : (
                      <span style={S.muted}>—</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <span style={S.verifiedBadge}>✓ Verified</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Main BankDeposits Page
// ════════════════════════════════════════════════════════════════════════════
export default function BankDeposits() {
  const [tab, setTab] = useState("pending"); // 'pending' | 'unverified' | 'verified'
  const [toast, setToast] = useState(null);

  const role = (localStorage.getItem("role") || "").replace(/"/g, "").trim();
  const isSuperAdmin = role === "SUPERADMIN";
  console.log(localStorage.getItem("role") === "SUPERADMIN"); // true வருதா?
  console.log(typeof localStorage.getItem("role")); // "string" வருதா?

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const FLOW = [
    { icon: "💵", label: "Cash Collected" },
    { icon: "🏦", label: "Deposited to Bank" },
    { icon: "🔍", label: "Pending Verification" },
    { icon: "✅", label: "Verified" },
  ];

  const flowColors = [
    "#22c55e",
    tab === "pending" ? "#3b82f6" : "#d1d5db",
    tab === "unverified" ? "#f59e0b" : "#d1d5db",
    tab === "verified" ? "#16a34a" : "#d1d5db",
  ];

  return (
    <div style={S.page}>
      <AccessControl
        allowedRoles={[
          "SUPERADMIN",
          "ADMIN",
          "ACCOUNTS",
          "NSM",
          "RSM",
          "ASM",
          "TSO",
        ]}
        redirectTo="/404"
      />

      <div style={S.wrap}>
        {/* Header */}
        <div style={S.headerTop}>
          <div style={S.headerIcon}>🏦</div>
          <h1 style={S.h1}>Bank Deposits</h1>
        </div>
        <p style={S.subtitle}>
          Track cash collection → bank deposit → superadmin verification → Excel
          export
        </p>

        {/* Flow indicator */}
        <div style={S.flow}>
          {FLOW.map((step, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  ...S.flowStep,
                  color: flowColors[i],
                  background:
                    i === 0 || flowColors[i] !== "#d1d5db"
                      ? `${flowColors[i]}18`
                      : "transparent",
                }}
              >
                <span>{step.icon}</span>
                <span>{step.label}</span>
              </div>
              {i < FLOW.length - 1 && <span style={S.flowArrow}>›</span>}
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={S.tabs}>
          <button
            style={{ ...S.tab, ...(tab === "pending" ? S.tabActive : {}) }}
            onClick={() => setTab("pending")}
          >
            🏦 Pending Deposits
          </button>
          {isSuperAdmin && (
            <button
              style={{ ...S.tab, ...(tab === "unverified" ? S.tabActive : {}) }}
              onClick={() => setTab("unverified")}
            >
              🔍 Verify Deposits
            </button>
          )}
          <button
            style={{ ...S.tab, ...(tab === "verified" ? S.tabActive : {}) }}
            onClick={() => setTab("verified")}
          >
            ✅ Verified Records
          </button>
        </div>

        {/* Tab content */}
        {tab === "pending" && <PendingDepositsTab showToast={showToast} />}
        {tab === "unverified" && isSuperAdmin && (
          <UnverifiedDepositsTab showToast={showToast} />
        )}
        {tab === "verified" && <VerifiedDepositsTab showToast={showToast} />}
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...S.toast,
            background: toast.ok ? "#1e293b" : "#7f1d1d",
            borderLeftColor: toast.ok ? "#22c55e" : "#ef4444",
          }}
        >
          {toast.ok ? "✅ " : "❌ "}
          {toast.msg}
        </div>
      )}
    </div>
  );
}